<template>
  <div class="home-page">
    <Swiper class="swiper-container" :options="swiperOptions" ref="homeSwiper">
      <!-- 第一页：欢迎页 -->
      <SwiperSlide class="slide welcome-slide" :class="{ 'has-custom-bg': homepageConfig.heroBackgroundUrl }"
        :style="homepageConfig.heroBackgroundUrl ? `background-image: url(${$http.getFullImageUrl(homepageConfig.heroBackgroundUrl)})` : ''">
        <div class="slide-content">
          <h1 class="main-title">{{ companyConfig.name || '内蒙古财达企业管理咨询有限公司' }}</h1>
          <p class="subtitle">{{ companyConfig.nameEn || `Inner Mongolia Caida Enterprise Management Consulting Co.,
            Ltd.` }}</p>
          <p class="slogan">{{ companyConfig.slogan || '立人立己 达人达己' }}</p>
        </div>
      </SwiperSlide>

      <!-- 第二页：企业信息 -->
      <SwiperSlide class="slide enterprises-slide">
        <div class="slide-content">
          <h2 class="section-title">入驻企业</h2>
          <div class="enterprises-grid" v-loading="loading">
            <div v-for="enterprise in enterprises" :key="enterprise.id" class="enterprise-card"
              @click="goToDetail(enterprise.id)">
              <div class="card-image"
                :style="enterprise.logo ? `background-image: url(${$http.getFullImageUrl(enterprise.logo)})` : ''">
                <div class="card-overlay">
                  <h3>{{ enterprise.name }}</h3>
                  <p v-if="enterprise.creditRating">信用评级：{{ enterprise.creditRating }}</p>
                </div>
              </div>
            </div>
          </div>
          <el-button type="primary" size="large" @click="goToApply" class="apply-btn">立即入驻</el-button>
        </div>
      </SwiperSlide>

      <!-- 第三页：工商税务 -->
      <SwiperSlide class="slide tax-slide">
        <div class="slide-content">
          <h2 class="section-title">工商税务服务</h2>
          <div class="service-content">
            <div class="service-item">
              <i class="el-icon-document"></i>
              <h3>工商注册</h3>
              <p>提供企业工商注册、变更、注销等服务</p>
            </div>
            <div class="service-item">
              <i class="el-icon-tickets"></i>
              <h3>税务代理</h3>
              <p>专业税务申报、税务筹划服务</p>
            </div>
            <div class="service-item">
              <i class="el-icon-folder"></i>
              <h3>财务咨询</h3>
              <p>企业财务管理、审计咨询服务</p>
            </div>
          </div>
          <el-button type="primary" size="large" @click="goToTaxAgency">了解更多</el-button>
        </div>
      </SwiperSlide>

      <!-- 第四页：交易信息 -->
      <SwiperSlide class="slide services-slide">
        <div class="slide-content">
          <h2 class="section-title">交易信息</h2>
          <div class="services-info">
            <p class="info-text">为企业提供全方位的交易信息服务</p>
            <div class="features">
              <div class="feature-item">
                <i class="el-icon-s-order"></i>
                <span>交易撮合</span>
              </div>
              <div class="feature-item">
                <i class="el-icon-data-analysis"></i>
                <span>市场分析</span>
              </div>
              <div class="feature-item">
                <i class="el-icon-connection"></i>
                <span>资源对接</span>
              </div>
            </div>
          </div>
          <el-button type="primary" size="large" @click="goToServices">查看详情</el-button>
        </div>
      </SwiperSlide>

      <!-- 分页指示器 -->
      <div class="swiper-pagination" slot="pagination"></div>
    </Swiper>

    <!-- 公告弹窗 -->
    <el-dialog :title="announcement.title || '系统公告'" :visible.sync="announcementVisible" width="60%"
      custom-class="announcement-dialog" append-to-body>
      <div class="announcement-content w-e-text" v-html="announcement.content"></div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="announcementVisible = false">我知道了</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'Home',
  components: {},
  data() {
    return {
      loading: false,
      enterprises: [],
      companyConfig: {},
      homepageConfig: {},
      themeConfig: {},
      swiperOptions: {
        direction: 'vertical',
        slidesPerView: 1,
        spaceBetween: 0,
        mousewheel: {
          releaseOnEdges: true,
          sensitivity: 1
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          type: "bullets"
        },
        speed: 800,
        autoplay: false
      },
      announcement: {},
      announcementVisible: false
    };
  },
  mounted() {
    this.loadConfigs();
    this.loadEnterprises();
  },
  methods: {
    async loadConfigs() {
      try {
        const [companyRes, homepageRes, themeRes] = await Promise.all([
          this.$http.get('/config/company'),
          this.$http.get('/config/homepage'),
          this.$http.get('/config/theme')
        ]);

        if (companyRes.success && companyRes.data) {
          this.companyConfig = companyRes.data;
        }

        if (homepageRes.success && homepageRes.data) {
          this.homepageConfig = homepageRes.data;
        }

        if (themeRes.success && themeRes.data) {
          this.themeConfig = themeRes.data;
        }

        // 获取公告
        this.loadAnnouncement();
      } catch (error) {
        console.error('加载配置失败:', error);
      }
    },

    async loadEnterprises() {
      this.loading = true;
      try {
        const response = await this.$http.get('/enterprises');
        if (response.data && response.data.length > 0) {
          this.enterprises = response.data.slice(0, 6);
        }
      } catch (error) {
        console.error('加载企业数据失败:', error);
        this.$message.error('加载企业数据失败');
      } finally {
        this.loading = false;
      }
    },

    async loadAnnouncement() {
      // 检查本会话是否已显示
      const shown = sessionStorage.getItem('announcement_shown');
      if (shown) return;

      try {
        const response = await this.$http.get('/public/announcement/active');
        if (response.success && response.data && response.data.isEnabled) {
          this.announcement = response.data;
          this.announcementVisible = true;
          sessionStorage.setItem('announcement_shown', 'true');
        }
      } catch (error) {
        console.error('加载公告失败:', error);
      }
    },

    goToDetail(id) {
      this.$router.push(`/enterprises/${id}`);
    },

    goToTaxAgency() {
      this.$router.push('/tax-agency');
    },

    goToServices() {
      this.$router.push('/services');
    },

    goToApply() {
      this.$router.push('/enterprise/apply');
    }
  }
};
</script>

<style lang="scss">
.home-page {
  width: 100%;
  height: calc(100vh - 60px);
  min-height: 500px;
}

.swiper-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.slide {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 1;
  }

  .slide-content {
    position: relative;
    z-index: 2;
    text-align: center;
    color: #fff;
    padding: 40px;
    max-width: 1200px;
    width: 100%;
  }
}

.welcome-slide {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e8ba3 100%);

  // 当有自定义背景图时，添加更深的遮罩
  &.has-custom-bg::before {
    background: rgba(0, 0, 0, 0.5);
  }

  .main-title {
    font-size: 48px;
    font-weight: bold;
    margin-bottom: 20px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
    animation: fadeInDown 1s;
  }

  .subtitle {
    font-size: 24px;
    margin-bottom: 30px;
    opacity: 0.95;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
    animation: fadeInUp 1s 0.3s both;
  }

  .slogan {
    font-size: 36px;
    font-weight: 300;
    margin-top: 40px;
    letter-spacing: 4px;
    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.2);
    animation: fadeIn 1s 0.6s both;
  }
}

.enterprises-slide {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  .section-title {
    font-size: 42px;
    margin-bottom: 40px;
    font-weight: bold;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }

  .enterprises-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 20px;

    .enterprise-card {
      height: 200px;
      border-radius: 12px;
      overflow: hidden;
      cursor: pointer;
      transition: all 0.3s ease;
      background: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      &:hover {
        transform: translateY(-8px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
      }

      .card-image {
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center;
        background-color: #f5f5f5;
        position: relative;

        .card-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.85), transparent);
          padding: 20px 15px;
          transform: translateY(100%);
          transition: transform 0.3s ease;

          h3 {
            font-size: 16px;
            margin: 0 0 5px 0;
            color: #fff;
            font-weight: 600;
          }

          p {
            font-size: 14px;
            margin: 0;
            color: #e0e0e0;
          }
        }
      }

      &:hover .card-overlay {
        transform: translateY(0);
      }
    }
  }

  .apply-btn {
    margin-top: 30px;
    padding: 15px 40px;
    font-size: 18px;
    border-radius: 30px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    transition: all 0.3s;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
    }
  }
}

.tax-slide {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);

  .section-title {
    font-size: 42px;
    margin-bottom: 40px;
    font-weight: bold;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }

  .service-content {
    display: flex;
    justify-content: center;
    gap: 40px;
    margin-bottom: 40px;

    .service-item {
      flex: 1;
      max-width: 300px;
      padding: 30px;
      background: rgba(255, 255, 255, 0.25);
      border-radius: 16px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.3);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.35);
        transform: translateY(-5px);
      }

      i {
        font-size: 48px;
        margin-bottom: 20px;
        display: block;
      }

      h3 {
        font-size: 24px;
        margin-bottom: 15px;
        font-weight: 600;
      }

      p {
        font-size: 16px;
        opacity: 0.95;
        line-height: 1.6;
      }
    }
  }
}

.services-slide {
  background: linear-gradient(135deg, #ee0979 0%, #ff6a00 100%);

  .section-title {
    font-size: 42px;
    margin-bottom: 40px;
    font-weight: bold;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }

  .services-info {
    margin-bottom: 40px;

    .info-text {
      font-size: 24px;
      margin-bottom: 40px;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
    }

    .features {
      display: flex;
      justify-content: center;
      gap: 60px;

      .feature-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 15px;
        padding: 20px;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(5px);
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.05);
        }

        i {
          font-size: 64px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        span {
          font-size: 20px;
          font-weight: 500;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
        }
      }
    }
  }
}

.swiper-pagination {
  position: absolute;
  right: 30px !important;
  left: auto !important;
  width: auto !important;
  z-index: 10;
}

.swiper-pagination-bullet {
  width: 12px;
  height: 12px;
  background: rgba(255, 255, 255, 0.5);
  opacity: 1;
  margin: 8px 0 !important;
  border-radius: 50%;
  display: block;
}

.swiper-pagination-bullet-active {
  background: #fff;
  width: 14px;
  height: 14px;
}

/* 只隐藏主滚动条但保留功能 */
::-webkit-scrollbar {
  width: 0px;
  height: 0px;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .home-page {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
  }

  .swiper-container {
    height: auto;
    overflow: visible;
  }

  .swiper-wrapper {
    flex-direction: column;
    height: auto !important;
  }

  .swiper-slide {
    height: auto !important;
    min-height: 100vh;
    padding: 60px 20px;
    box-sizing: border-box;
  }

  .slide .slide-content {
    padding: 20px;
  }

  /* 欢迎页适配 */
  .welcome-slide .main-title {
    font-size: 32px;
  }

  .welcome-slide .subtitle {
    font-size: 18px;
  }

  .welcome-slide .slogan {
    font-size: 24px;
    margin-top: 30px;
    letter-spacing: 2px;
  }

  /* 企业展示页适配 */
  .enterprises-slide .section-title,
  .tax-slide .section-title,
  .services-slide .section-title {
    font-size: 32px;
    margin-bottom: 30px;
  }

  .enterprises-slide .enterprises-grid {
    grid-template-columns: repeat(1, 1fr);
    gap: 15px;
  }

  /* 税务服务页适配 */
  .tax-slide .service-content {
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }

  .tax-slide .service-item {
    width: 100%;
    max-width: 100%;
  }

  /* 交易信息页适配 */
  .services-slide .services-info .features {
    flex-direction: column;
    gap: 30px;
  }

  .swiper-pagination {
    display: none;
    /* 移动端隐藏分页器 */
  }
}

/* 公告弹窗样式 */
.announcement-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    background: #f8f9fa;
    padding: 20px;
    border-bottom: 1px solid #eee;

    .el-dialog__title {
      font-weight: bold;
      color: #303133;
    }
  }

  .announcement-content {
    padding: 10px 0;
    max-height: 50vh;
    overflow-y: auto;
    font-size: 16px;
    line-height: 1.8;
    color: #606266;

    /* 富文本里的图片自适应 */
    img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 10px auto;
    }
  }
}
</style>
